import {
  ADVANCED_FIELD_KEYS,
  formatFormulaFieldValue,
  FormulaFieldKey,
} from "portal/utils/shootTime";
import { Card, Divider, Typography } from "@mui/material";
import { classes } from "portal/theme/theme";
import { Formula } from "protos/almanac/almanac";
import { FORMULA_FIELD_CONSTRAINTS } from "portal/utils/almanac";
import { NumberInput } from "common/components/NumberInput";
import { useTranslation } from "react-i18next";
import { WhenTrue } from "common/utils/types";
import React, { FunctionComponent } from "react";

type AdvancedShootTimeValuesProps = {
  formula: Formula;
  outlined?: boolean;
} & WhenTrue<
  "canEdit",
  {
    onFieldChange: (field: FormulaFieldKey, value: number) => void;
    onFieldValidityChange: (field: FormulaFieldKey, isValid: boolean) => void;
  }
>;

export const AdvancedShootTimeValues: FunctionComponent<
  AdvancedShootTimeValuesProps
> = ({
  formula,
  outlined = true,
  canEdit,
  onFieldChange,
  onFieldValidityChange,
}) => {
  const { t } = useTranslation();

  const fields = ADVANCED_FIELD_KEYS.map((key, index) => {
    const { min, max, step } = FORMULA_FIELD_CONSTRAINTS[key];
    return (
      <div key={key}>
        {index > 0 && <Divider />}
        <div
          className={classes(
            "flex items-center justify-between gap-4 py-3",
            outlined ? "px-4" : "px-0"
          )}
        >
          <div className="flex flex-col gap-0.5 min-w-0">
            <Typography className="font-semibold">
              {t(
                `views.categoryTemplates.shootTime.advancedFields.${key}.label`
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                `views.categoryTemplates.shootTime.advancedFields.${key}.description`
              )}
            </Typography>
          </div>
          {canEdit ? (
            <NumberInput
              value={formula[key]}
              min={min}
              max={max}
              step={step}
              required
              onChange={(val) => {
                if (val === undefined) {
                  return;
                }
                onFieldChange(key, val);
              }}
              onValidityChange={(isValid) => {
                onFieldValidityChange(key, isValid);
              }}
              textFieldProps={{
                className: "min-w-16 [&_.MuiInputBase-input]:font-semibold",
              }}
            />
          ) : (
            <Typography className="font-semibold shrink-0">
              {formatFormulaFieldValue(t, key, formula[key])}
            </Typography>
          )}
        </div>
      </div>
    );
  });

  if (!outlined) {
    return <div className="w-full">{fields}</div>;
  }

  return (
    <Card variant="outlined" className="rounded-lg overflow-hidden">
      {fields}
    </Card>
  );
};
